/**
 * Futures Calculator - Core Logic
 * Isolated Margin Model
 */

/**
 * Calculate theoretical liquidation distance
 * @param {number} L - Leverage
 * @param {number} mm - Maintenance margin (e.g., 0.005)
 * @returns {number} p_theoretical
 */
export const calculatePTheoretical = (L, mm) => {
  return 1 / L - mm;
};

/**
 * Calculate required leverage for specific liquidation distance
 * @param {number} p_liq - Desired liquidation distance
 * @param {number} mm - Maintenance margin
 * @returns {number} L_required
 */
export const calculateLRequired = (p_liq, mm) => {
  return 1 / (p_liq + mm);
};

/**
 * Calculate single step
 * @param {object} params
 * @returns {object} Step result with warnings
 */
export const calculateStep = ({
  stepIndex,
  currentBalance,
  direction,
  entry,
  exit,
  L_user,
  p_liq_percent,
  mm = 0.005,
  feeRate = 0,
  useMaxDeposit = true,
  capitalFraction = 1.0,
  autoAdjustLeverage = true
}) => {
  const warnings = [];
  let L_eff = L_user;
  
  // Convert p_liq from percent to decimal
  const p_liq = p_liq_percent > 0 ? p_liq_percent / 100 : 0;
  
  // Validate inputs
  if (entry <= 0 || exit <= 0) {
    throw new Error('Entry and exit prices must be positive');
  }
  if (L_user < 1) {
    throw new Error('Leverage must be >= 1');
  }
  if (p_liq_percent < 0 || p_liq_percent >= 100) {
    throw new Error('p_liq must be in range [0, 100)');
  }
  
  // Convert p_liq from percent to decimal (already done above)
  
  // Calculate theoretical liquidation distance with user's leverage
  const p_theoretical = calculatePTheoretical(L_user, mm);
  
  // Always use user's leverage for the trade
  L_eff = L_user;
  
  // Calculate required leverage for desired p_liq (for display purposes)
  let L_required_for_p_liq = L_user;
  if (p_liq > 0) {
    L_required_for_p_liq = calculateLRequired(p_liq, mm);
  }
  
  // Calculate position size
  // If p_liq is specified, use L_required_for_p_liq instead of user's leverage
  const L_for_calculation = (p_liq > 0) ? L_required_for_p_liq : L_eff;
  
  let V_nominal;
  if (useMaxDeposit) {
    V_nominal = currentBalance * L_for_calculation;
  } else {
    V_nominal = currentBalance * capitalFraction * L_for_calculation;
  }
  
  // Calculate initial margin (use the same leverage as for V_nominal calculation)
  const initial_margin = V_nominal / L_for_calculation;
  
  // Check if initial margin exceeds available balance
  if (initial_margin > currentBalance) {
    const reduction_factor = currentBalance / initial_margin;
    V_nominal = V_nominal * reduction_factor;
    warnings.push(`Недостаточно депозита. Объём уменьшен в ${(1/reduction_factor).toFixed(2)} раз`);
  }
  
  // Recalculate initial margin after potential adjustment
  const im = V_nominal / L_for_calculation;
  
  // Calculate PnL
  let PnL_USD;
  if (direction === 'long') {
    PnL_USD = V_nominal * (exit / entry - 1);
  } else if (direction === 'short') {
    PnL_USD = V_nominal * (1 - exit / entry);
  } else {
    throw new Error('Direction must be "long" or "short"');
  }
  
  // Calculate fees
  const fee = feeRate * V_nominal;
  
  // Calculate final balance
  const balance_after = currentBalance + PnL_USD - fee;
  
  // Calculate liquidation price and max BTC
  // IMPORTANT: Liquidation price must be calculated using ACTUAL effective leverage (L_for_calculation)
  // not the simplified p_liq formula!
  
  let liquidation_price;
  let max_btc_amount;
  
  // Calculate liquidation price using actual effective leverage
  if (direction === 'long') {
    liquidation_price = entry * (1 - (1 / L_for_calculation - mm));
  } else {
    liquidation_price = entry * (1 + (1 / L_for_calculation - mm));
  }
  
  // Max BTC calculation for CROSS MARGIN
  // For cross margin: use entire balance as margin
  // Position size = Balance × Leverage
  // Where leverage is calculated from desired p_liq
  
  if (p_liq > 0) {
    // Calculate leverage needed for desired p_liq
    // For cross margin: p_liq = (1 / leverage) - maintenance_margin
    // Therefore: leverage = 1 / (p_liq + maintenance_margin)
    const leverage_for_p_liq = 1 / (p_liq + mm);
    
    // Max BTC amount using entire balance (cross margin)
    // Position = Balance × Leverage
    max_btc_amount = (currentBalance * leverage_for_p_liq) / entry;
    
    // Use 95% of balance to leave buffer for fees
    max_btc_amount = max_btc_amount * 0.95;
  } else {
    // Use user's leverage directly
    max_btc_amount = (currentBalance * L_eff) / entry;
    max_btc_amount = max_btc_amount * 0.95;
  }
  
  // Calculate loss to liquidation
  const loss_to_liq_USD = V_nominal * (1 / L_eff - mm);
  
  return {
    stepIndex,
    direction,
    entry: Number(entry.toFixed(2)),
    exit: Number(exit.toFixed(2)),
    leverage: L_user, // Original user leverage
    p_liq: p_liq_percent, // Original p_liq percent
    L_eff: Number(L_eff.toFixed(2)),
    V_nominal: Number(V_nominal.toFixed(2)),
    initial_margin: Number(im.toFixed(2)),
    p_theoretical: Number(p_theoretical.toFixed(6)),
    liquidation_price: Number(liquidation_price.toFixed(2)),
    max_btc_amount: Number(max_btc_amount.toFixed(8)),
    PnL_USD: Number(PnL_USD.toFixed(2)),
    fee: Number(fee.toFixed(2)),
    balance_after: Number(balance_after.toFixed(2)),
    loss_to_liq_USD: Number(loss_to_liq_USD.toFixed(2)),
    warnings
  };
};

/**
 * Simulate sequence of steps
 * @param {object} params
 * @returns {object} Simulation results
 */
export const simulateSequence = ({
  initialBalance,
  marginMode = 'isolated',
  steps
}) => {
  if (initialBalance <= 0) {
    throw new Error('Initial balance must be positive');
  }
  
  const mm = 0.005; // Fixed maintenance margin
  const feeRate = 0; // No fees
  
  let currentBalance = initialBalance;
  const results = [];
  
  // For cross margin, track total positions
  let totalUnrealizedPnL = 0;
  
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    
    try {
      const result = calculateStep({
        stepIndex: i + 1,
        currentBalance: marginMode === 'isolated' ? currentBalance : initialBalance,
        direction: step.direction,
        entry: step.entry,
        exit: step.exit,
        L_user: step.leverage,
        p_liq_percent: step.p_liq || 0,
        mm,
        feeRate,
        useMaxDeposit: step.useMaxDeposit !== undefined ? step.useMaxDeposit : true,
        capitalFraction: step.capitalFraction || 1.0,
        autoAdjustLeverage: step.autoAdjustLeverage !== undefined ? step.autoAdjustLeverage : true
      });
      
      results.push(result);
      
      if (marginMode === 'isolated') {
        currentBalance = result.balance_after;
      } else {
        // Cross margin: accumulate PnL
        totalUnrealizedPnL += result.PnL_USD;
      }
      
      // Stop if balance becomes negative or zero
      if (currentBalance <= 0 && marginMode === 'isolated') {
        results.push({
          stepIndex: i + 2,
          error: 'Баланс обнулён. Дальнейшие шаги невозможны.'
        });
        break;
      }
    } catch (error) {
      results.push({
        stepIndex: i + 1,
        error: error.message
      });
      break;
    }
  }
  
  const finalBalance = marginMode === 'isolated' ? currentBalance : initialBalance + totalUnrealizedPnL;
  const totalROI = ((finalBalance - initialBalance) / initialBalance) * 100;
  
  return {
    steps: results,
    initialBalance,
    finalBalance: Number(finalBalance.toFixed(2)),
    totalROI: Number(totalROI.toFixed(2)),
    absoluteProfit: Number((finalBalance - initialBalance).toFixed(2)),
    marginMode
  };
};

/**
 * Export results to CSV
 * @param {object} results - Simulation results
 * @returns {string} CSV string
 */
export const exportToCSV = (results) => {
  const headers = [
    'Шаг',
    'Направление',
    'Вход',
    'Выход',
    'Плечо',
    'Цена ликвидации',
    'Макс BTC',
    'Объём',
    'Маржа',
    'PnL',
    'Баланс',
    'Предупреждения'
  ];
  
  const rows = results.steps.map((step, idx) => {
    if (step.error) {
      return [step.stepIndex, 'ОШИБКА', '', '', '', '', '', '', '', '', '', step.error];
    }
    
    // Calculate display balance (for cross margin)
    let displayBalance = step.balance_after;
    if (results.marginMode === 'cross') {
      const cumulativePnL = results.steps
        .slice(0, idx + 1)
        .filter(s => !s.error)
        .reduce((sum, s) => sum + s.PnL_USD, 0);
      displayBalance = results.initialBalance + cumulativePnL;
    }
    
    return [
      step.stepIndex,
      step.direction,
      step.entry,
      step.exit,
      step.L_eff,
      step.liquidation_price || 'N/A',
      step.max_btc_amount || 'N/A',
      step.V_nominal,
      step.initial_margin,
      step.PnL_USD,
      displayBalance.toFixed(2),
      step.warnings.join('; ')
    ];
  });
  
  const csv = [headers, ...rows]
    .map(row => row.map(cell => `"${cell}"`).join(','))
    .join('\n');
  
  return csv;
};